import { getConcretePathForAttribute } from "csi/core/extendedPathService";
import Key from "csi/core/Key";
import { getEnumList } from "csi/reflection/reflection";
import { ATTR_MODIFIED_AT, ATTR_MODIFIED_BY, ATTR_RECENT_CHILD_COMMENTS, ATTR_RECENT_COMMENTS, TYPE_VIEW } from "csi/reflection/reflectionConstants";
import reflectionHints from "csi/reflectionHints";
import { isDefined } from "csi/utility/coreUtility";
import { addToGroupedObject } from "csi/utility/objectUtility";
import xml from "csi/xml";
import ChangeNode from "csi/xml/ChangeNode";
import Writer from "csi/xml/Writer";
import { compact, forEach, isEmpty, isEqual, transform } from "lodash";
import { CustomViewVersion, ICustomViewAttribute } from "src/c8-platform/type/customView";
import { ATTR_NAME, C8Node, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeKey, cnl } from "src/c8-platform/type/value";
import { migrateTableKeysV791 } from "../attributes/attributeKeysService";
import CustomViewAttributesStore from "./attributes/CustomViewAttributesStore";

const {
	PROP_CREATOR,
	PROP_CREATE_TIME,
	PROP_NAME,
	PROP_PUBLISHED_BY,
	PROP_PUBLISHED_TIME,
} = xml;

/*
 * There are three possibile issues when validating a custom view key:
 * 1. key is to be removed
 * 2. key is to be (partially) replaced with another
 * 3. key is not valid, but not sure whether it should be removed or replaced - needs to be resolved manually
 */
interface CustomViewMigrateKeyReportItem {
	/** custom view containers where item was reported */
	containerUrls?: Set<cnl>;
	
	/** key that has issue */
	key: C8AttributeKey;
	
	/** string to replace search string with */
	replace?: string;
	
	/** options for replace string */
	replaceOptions?: string[];

	/** string to search for if key is to be replaced; if empty, then key will be removed */
	search?: string;
}

interface CustomViewMigrationKeyFinalReport {
	/** urls to address in report */
	containerUrls?: cnl[];

	/** report results */
	results: CustomViewMigrationKeyReportSection[];
}

type CustomViewMigrationKeyReportSection = [sectionName: string, items: CustomViewMigrateKeyReportItem[]];
type CustomViewMigrationKeyValidationResult = CustomViewMigrateKeyReportItem | undefined;
type CustomViewMigrationKeyValidation = [name: string, func: (containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore) => CustomViewMigrationKeyValidationResult];
type CustomViewMigrationKeyPair = [key: C8AttributeKey, keyObj: Key];

const validations: CustomViewMigrationKeyValidation[] = [];

/** escape key for use in search (and replace) operation */
function escapeKey(key: C8AttributeKey): string {
	return key.replace(/\(/g, "\\(")
		.replace(/\)/g, "\\)")
		.replace(/\{/g, "\\{")
		.replace(/\}/g, "\\}");
}

/** return true if Heading configured for given key */
function hasHeading(key: Key, attributesStore: CustomViewAttributesStore): boolean {
	const configuredHeading = attributesStore.mainLevel.specBuilder.configuredHeadingMapByKey.get(key.headingKey);
	return !!configuredHeading;
}

/** return item without urls for comparison */
function itemComparisonValue(item: CustomViewMigrateKeyReportItem): Object {
	const { key, search, replace } = item;
	return search ? { replace, search } : { key };
}

/** return id for item's containers */
function itemContainerId(item: CustomViewMigrateKeyReportItem): string {
	const { containerUrls } = item;
	if (containerUrls) {
		return Array.from(containerUrls).sort()
			.join(",");
	}

	return "";
}

/** return id for item */
function itemId(item: CustomViewMigrateKeyReportItem): string {
	// if item references a container, the container should not be part of the id, but the
	// fact that it is container-specific should
	const { containerUrls, key, search } = item;
	return (containerUrls ? "CURL#" : "") + (search || key);
}

/** return item to remove key - only pass container url if issue is view-specific */
export function createRemoveItem(key: C8AttributeKey, containerUrl?: cnl): CustomViewMigrateKeyReportItem {
	return {
		containerUrls: containerUrl ? new Set([containerUrl]) : undefined,
		key,
	};
}

/** return item that includes a search key and optionally a replace key - if no search key is provided, then entire key needs to be replaced */
export function createSearchItem(key: C8AttributeKey, containerUrl?: cnl, search?: string, replace?: string): CustomViewMigrateKeyReportItem {
	return {
		containerUrls: containerUrl ? new Set([containerUrl]) : undefined,
		key,
		replace,
		search: search ? escapeKey(search) : `^${escapeKey(key)}$`,
	};
}

export class CustomViewMigrationKeyReport {
	/** keys with issues by section that are duplicates of existing results */
	readonly duplicates: CustomViewMigrationKeyReportSection[] = [];

	/** keys with issues by section */
	readonly results: CustomViewMigrationKeyReportSection[] = [];
	
	/** quick lookup of items in report by id */
	private readonly _lookup: Record<string, CustomViewMigrateKeyReportItem[]> = {};

	/** return list of keys to remove */
	get removeKeys(): C8AttributeKey[] {
		const keys: C8AttributeKey[] = [];

		this.results.forEach(([name, items]) => {
			items.forEach(({ key, search }) => {
				if (!search) {
					keys.push(key);
				}
			});
		});
		
		return keys;
	}

	/** unique list of container ids referenced by items in report */
	get containerIds(): string[] {
		const set = new Set<string>();

		this.results.forEach(([sectionName, items]) => {
			items.forEach((item) => {
				set.add(itemContainerId(item));
			});
		});

		return Array.from(set).sort();
	}

	/** final reports by container */
	get finalReportsByContainer(): CustomViewMigrationKeyFinalReport[] {
		return this.containerIds.map(this.getFinalReport);
	}

	/** merge one report with another */
	addReport = (report: CustomViewMigrationKeyReport): void => {
		for (const [section, items] of report.results) {
			this.addSection(section, items);
		}
	};

	/** add items to given section */
	addSection = (section: string, items: CustomViewMigrateKeyReportItem[]): void => {
		if (!items.length) {
			return;
		}
		
		const { duplicates, results, _lookup, _mergeSection } = this;
		const duplicateItems: CustomViewMigrateKeyReportItem[] = [];
		const resultItems: CustomViewMigrateKeyReportItem[] = [];

		items.forEach((item) => {
			const id = itemId(item);
			const existing = _lookup[id];
			const value = itemComparisonValue(item);
			const found = existing?.some((existingItem) => {
				if (isEqual(itemComparisonValue(existingItem), value)) {
					// add containerUrls from new item to existing item
					if (existingItem.containerUrls) {
						existingItem.containerUrls = new Set([...existingItem.containerUrls, ...item.containerUrls]);
					}

					return true;
				} else if (!item.containerUrls) {
					// a global issue has a different solution than another, so track as duplicate
					console.warn("Custom View Error Duplicate: ", existingItem, item);
					duplicateItems.push(item);
					return true;
				}

				return false;
			});

			// item is new
			if (!found) {
				resultItems.push(item);
				addToGroupedObject(_lookup, id, item);
			}
		});

		_mergeSection(section, duplicateItems, duplicates);
		_mergeSection(section, resultItems, results);
	};

	/** return report for given container id */
	getFinalReport = (containerId: string): CustomViewMigrationKeyFinalReport => {
		const containerUrls: cnl[] = containerId ? containerId.split(",") : [];
		const results: CustomViewMigrationKeyReportSection[] = compact(this.results.map(([sectionName, items]) => {
			const filteredItems = items.filter((item) => itemContainerId(item) === containerId);
			if (filteredItems.length) {
				return [sectionName, filteredItems];
			}
		}));

		return { containerUrls, results };
	};

	/** merge section items into report */
	_mergeSection = (section: string, items: CustomViewMigrateKeyReportItem[], results: CustomViewMigrationKeyReportSection[]): void => {
		if (!items.length) {
			return;
		}

		const sectionItem = results.find(([sectionName, items]) => sectionName === section);
		if (sectionItem) {
			sectionItem[1].push(...items);
		} else {
			results.push([section, items]);
		}
	};
}

/** return report for migrating custom view keys */
export function getCustomViewKeyMigrationReport(containerUrl: cnl, store: CustomViewAttributesStore, cv: C8Node): CustomViewMigrationKeyReport {
	const report = new CustomViewMigrationKeyReport();
	const keys: Record<string, C8AttributeKey> = { ...cv.Keys };
	const options = cv.Options ? JSON.parse(cv.Options) : {};
	const missingMatrixKeys: C8AttributeKey[] = [];
	const renamedMatrixKeys: Record<C8AttributeKey, C8AttributeKey> = {};
	let keyValues = Object.values(keys);
	
	// find matrix keys that are renamed and removed
	if ((options.VR < CustomViewVersion.V791) && cv.Attributes.length) {
		const attributeKeys: C8AttributeKey[] = compact(cv.Attributes.map((index: string) => keys[index]));
		const migrateOptions = { attributeKeys };
		const items: CustomViewMigrateKeyReportItem[] = [];

		migrateTableKeysV791(migrateOptions, store.mainLevel.specBuilder, renamedMatrixKeys, missingMatrixKeys);
		
		forEach(renamedMatrixKeys, (targetKey, sourceKey) => {
			items.push(createSearchItem(sourceKey, containerUrl, null, targetKey));
		});
		
		report.addSection("Invalid matrix key", items);

		if (!isEmpty(renamedMatrixKeys)) {
			keyValues = keyValues.map((attributeKey) => renamedMatrixKeys[attributeKey] || attributeKey);
		}
	}

	// run remaining validations
	validateCustomViewKeys(report, keyValues, store, containerUrl);

	return report;
}

/** return true if given key is valid */
export function isCustomViewKeyValid(attributeKey: C8AttributeKey, store: CustomViewAttributesStore): boolean {
	const key = new Key({ dpkey: attributeKey }, true);

	return validations.every(([name, func]) => {
		const item = func(null, attributeKey, key, store);
		if (item) {
			console.warn(`Invalid key ${item.key}`);
			return false;
		}

		return true;
	});
}

/** remove invalid keys from the given set of keys */
export function removeInvalidAttributeKeys(attributeKeys: ICustomViewAttribute[], attributesStore: CustomViewAttributesStore, forHidden?: boolean): ICustomViewAttribute[] {
	return transform(attributeKeys, (result, groupOrKey) => {
		if (typeof groupOrKey === "string") {
			if (isCustomViewKeyValid(groupOrKey, attributesStore)) {
				result.push(groupOrKey);
			}
		} else {
			const { groupKey, keys } = groupOrKey;

			if (!groupKey || isCustomViewKeyValid(groupKey, attributesStore)) {
				const validKeys = keys.filter((key) => isCustomViewKeyValid(key, attributesStore));
				if (forHidden || validKeys.length) {
					result.push({
						...groupOrKey,
						keys: validKeys,
					});
				}
			}
		}
	}, []);
}

/** validate given keys and add any validation issues to report */
export function validateCustomViewKeys(report: CustomViewMigrationKeyReport, keys: C8AttributeKey[], store: CustomViewAttributesStore, containerUrl?: cnl): void {
	let keyPairs: CustomViewMigrationKeyPair[] = keys.map((attributeKey: C8AttributeKey) => {
		const key = new Key({ dpkey: attributeKey }, true);
		return [attributeKey, key];
	});

	for (const [name, func] of validations) {
		const items: CustomViewMigrateKeyReportItem[] = [];
		const validatedPairs: (CustomViewMigrationKeyPair | undefined)[] = keyPairs.map((keyPair) => {
			const item = func(containerUrl, keyPair[0], keyPair[1], store);
			if (item) {
				const { key, replace, search } = item;

				items.push(item);

				if (replace) {
					// if replace string is known, then make the replacement and continue with other validations
					const validatedKey = key.replace(new RegExp(search), replace);
					const validatedKeyObj = Key.toKey(validatedKey);
					return [validatedKey, validatedKeyObj];
				} else {
					// if replace string is not known, or key is to be removed, then filter out key
					return;
				}
			}

			return keyPair;
		});

		keyPairs = compact(validatedPairs);

		report.addSection(name, items);
	}
}

/** write operations for issues in report */
export function writeMigrationKeyReportOperations(writer: Writer, targetClass: C8NodeType, report: CustomViewMigrationKeyReport, forceRemove?: boolean, sectionComment?: string): void {
	const { finalReportsByContainer } = report;
	let bFirst = true;

	finalReportsByContainer.forEach(({ containerUrls, results }) => {
		let cn: ChangeNode;

		const change = (): ChangeNode => {
			if (!cn) {
				if (bFirst) {
					writer.addComment(targetClass);
					bFirst = false;
				}

				const query = writer.createQuery();
				const qand = query.And()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, TYPE_VIEW)
					.Attr("TargetClass", xml.OP_EQUALS, targetClass);

				if (containerUrls.length > 1) {
					const qor = qand.Or();
					containerUrls.forEach((url) => {
						qor.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, url);
					});
				} else if (containerUrls.length) {
					qand.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, containerUrls[0]);
				}

				cn = writer.changeQuery(query);
				writer.flushChanges();
			}

			return cn;
		};

		results.forEach(([name, items]) => {
			const removeSet = new Set<C8AttributeKey>();
			const searchMap = new Map<string, string>();
			let b = false;

			items.forEach(({ key, replace, search }) => {
				if (!search || (forceRemove && !replace)) {
					// if item is search without any replace string, then force remove if specified
					removeSet.add(key);
					b = true;
				} else if (!forceRemove) {
					// search and replace only if not force removing (pre-migration)
					searchMap.set(search, replace);
					b = true;
				}
			});


			if (b) {
				const cn = change();
				if (sectionComment) {
					cn.comment(sectionComment);
				}
				cn.comment(name);

				if (removeSet.size) {
					const keys = Array.from(removeSet).sort();
					cn.changeAttribute("Keys", xml.ATTR_STRINGLIST, keys, false, true);
				}

				const searchKeys = Array.from(searchMap.keys()).sort();
				searchKeys.forEach((key) => {
					const replace = searchMap.get(key);
					if (replace) {
						cn.searchAndReplace("Keys", key, replace);
						searchMap.delete(key);
					}
				});
				const emptyReplaces = Array.from(searchMap.keys()).sort();
				if (emptyReplaces.length > 0) {
					cn.beginComment("Tool could not determine Replace path");
				}
				emptyReplaces.forEach((key) => {
					cn.searchAndReplace("Keys", key, searchMap.get(key));
				});
				if (emptyReplaces.length > 0) {
					cn.endComment();
				}
			}
		});
	});
}

/** validate if key is properly formatted */
export function validateSyntax(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	if (!key.toString()) {
		return createRemoveItem(attributeKey);
	}
}

/** validate if key's path exists in the model */
export function validatePathInModel(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	if (key.invalidPath) {
		return createRemoveItem(attributeKey);
	}
}

/** validate if key's path is typed */
export function validatePathTyped(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	if (!key.isTyped) {
		return createRemoveItem(attributeKey);
	}	
}	

/** validate if types in key's path changed from original */
export function validatePathChangedType(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { extendedPath, originalOwner, originalTypedPath } = key;
	if (originalTypedPath) {
		if (originalOwner) {
			return createSearchItem(
				attributeKey,
				attributesStore.hasContextualPathHints ? containerUrl : null,
				null,
				key.typedString,
			);
		} else {
			return createSearchItem(
				attributeKey,
				attributesStore.hasContextualPathHints ? containerUrl : null,
				`:${originalTypedPath}:`,
				`:${extendedPath.value}:`,
			);
		}
	}
}

/** validate if key's path is omitted */
export function validatePathNotOmitted(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	if (attributesStore.getContainerForKey(key, true)) {
		return;
	}

	const { extendedPath, path } = key;
	const { startType, value } = extendedPath;
	let result: CustomViewMigrationKeyValidationResult;

	attributesStore.levels.some(({ specBuilder }) => {
		const { defaultPathHints, defaultSourcePath, defaultSourceType } = specBuilder;

		if (defaultSourceType === startType) {
			const ep = defaultSourcePath.plus(path);
			if (ep.isOmitted) {
				// normally omitted paths have an alternative, so search rather than remove
				result = createSearchItem(attributeKey, defaultPathHints ? containerUrl : null, `:${value}:`);
				return true;
			}
		}
	});

	return result;
}

/** validate if path is available in the view */
export function validatePathInView(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { getContainerForKey, hasLevelForType, matrixSourceContainerMap } = attributesStore;
	const { candidateMatrixGroupId, extendedPath } = key;

	// could not be an attribute key, so only create issue if start class matches that of one of the levels
	if (hasLevelForType(extendedPath.startType) && !getContainerForKey(key, true)) {
		// check one last time to see if this is a matrix group key
		const matrixContainer = matrixSourceContainerMap[candidateMatrixGroupId];
		if (matrixContainer?.sourceMatrixGroupSpec.uniqueId !== attributeKey) {
			return createSearchItem(attributeKey, containerUrl, `:${extendedPath.value}:`);
		}
	}
}

/** validate if keys in matrix are valid */
export function validateKeyInMatrix(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { extendedPath, matrix } = key;

	if (matrix) {
		const { configuredMatrixGroupSpecs } = attributesStore.mainLevel.specBuilder;
		const spec = configuredMatrixGroupSpecs.find((spec) => spec.matrixGroupId === matrix);

		if (!spec || !extendedPath.isAlongPath(spec.matrixExtendedPath)) {
			return createRemoveItem(attributeKey, containerUrl);
		}
	}
}

/** validate if attribute is in the model */
export function validateAttributeExists(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { extendedPath, id, reflectionAttribute } = key;

	if (!id) {
		// only concerned with attributes here
		return;
	} else if (id === xml.ATTR_NAME) {
		// Node Name is only allowed on domain objects
		if (extendedPath.isPathToSecondary) {
			const replaceKey = key.copy({
				extendedPath: extendedPath.domainPath,
			});
			return createSearchItem(attributeKey, null, null, replaceKey.typedString);
		}

		return;
	} else if (id === xml.ATTR_TYPE) {
		// Node Type is only allowed when configured in view
		if (hasHeading(key, attributesStore)) {
			return;
		}
		
		return createRemoveItem(attributeKey, containerUrl);
	} else if (reflectionAttribute?.isSecondary) {
		// secondary attributes should not be displayed unless in view
		if (hasHeading(key, attributesStore)) {
			return;
		}
		
		return createRemoveItem(attributeKey, containerUrl);
	} else if (reflectionAttribute) {
		// If id matches ATTR_MODIFIED_AT or ATTR_MODIFIED_BY,then no need to check
		if (id === ATTR_MODIFIED_AT || id === ATTR_MODIFIED_BY ) {
			return;
		}

		// TODO: consider path hints for extended path (and at multiple levels) so we don't replace with omitted path?
		const newExtendedPath = getConcretePathForAttribute(id, extendedPath);
		if (newExtendedPath === extendedPath) {
			// attribute exists along existing path
			return;
		}
		
		// replacement found
		const replaceKey = key.copy({
			extendedPath: newExtendedPath,
		});
		return createSearchItem(attributeKey, containerUrl, null, replaceKey.typedString);
	} else {
		// attribute does not exist
		// TODO: find replacement options - if found, then search, otherwise remove?
		return createSearchItem(attributeKey);
	}
}

/** validate if attribute is enabled */
export function validateAttributeEnabled(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	if (key.attributeIsDisabled) {
		return createRemoveItem(attributeKey);
	}
}

/** validate if attribute is visible - export views allow all hidden attributes, while some hidden attributes may be explicitly added to the view to allow them */
export function validateAttributeVisible(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	if (!attributesStore.options.allowHidden && key.reflectionAttribute?.isHidden && !hasHeading(key, attributesStore)) {
		return createRemoveItem(attributeKey, containerUrl);
	}
}

/** validate if only CR and CT properties used */
export function validateProperty(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { extendedPath, prop } = key;

	if (!prop) {
		return;
	}

	// if property configured in view, then it's fine
	if (hasHeading(key, attributesStore)) {
		return;
	}

	const { domainPath, value } = extendedPath;

	if (prop === PROP_NAME) {
		// if option set to allow name property on starting class, then no issue
		if (attributesStore.options.useNamePropInPlaceOfNodeNameAttr && !domainPath.standardPath) {
			return;
		}

		// replace with Node Name attribute
		const replaceKey = key.copy({
			extendedPath: domainPath,
			id: ATTR_NAME,
			prop: null,
		});

		return createSearchItem(attributeKey, containerUrl, null, replaceKey.toString());
	} else if ((prop === PROP_PUBLISHED_BY) || (prop === PROP_PUBLISHED_TIME)) {
		// replace with Modified attribute, and ensure path is to domain class
		const replaceKey = key.copy({
			extendedPath: domainPath,
			id: (prop === PROP_PUBLISHED_BY) ? ATTR_MODIFIED_BY : ATTR_MODIFIED_AT,
			prop: null,
		});

		return createSearchItem(attributeKey, containerUrl, null, replaceKey.toString());
	} else if ((prop === PROP_CREATOR) || (prop === PROP_CREATE_TIME)) {
		// ensure path is to domain class
		if (domainPath.value !== value) {
			const replaceKey = key.copy({
				extendedPath: domainPath,
			});
			
			return createSearchItem(attributeKey, containerUrl, null, replaceKey.toString());
		}
	} else {
		// any other properties are errors and should be removed
		return createRemoveItem(attributeKey, containerUrl);
	}
}	

/** validate custom named heading is in view */
export function validateName(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { attributeGroup, isForShowingReferencedImages, isGroupByRefKey, isGroupKey, name, reflectionAttributeDestination } = key;
	
	if (isGroupKey) {
		// remove group key if attribute group does not exist
		if (!attributeGroup) {
			return createRemoveItem(attributeKey);
		}
	} else if (isForShowingReferencedImages) {
		// remove key if relationship is not to an image container
		if (!reflectionAttributeDestination?.isImageContainer) {
			return createRemoveItem(attributeKey);
		}
	} else if (name && !isGroupByRefKey && !hasHeading(key, attributesStore)) {
		// TODO: should we search instead? It's possible the Heading was changed to something else, or the custom name was removed.
		// However, we should encourage the devs to write this operation when working on the story, rather than wait for customer to migrate
		return createRemoveItem(attributeKey, containerUrl);
	}
}

/** treat "Default" string as empty for printPaper and printOrientation */
export function validatePrintPaperAndOrientation(values: {printOrientation: string; printPaper: string}): void {
	if (values.printPaper === "Default") {
		values.printPaper = "";
	}
	if (values.printOrientation === "Default") {
		values.printOrientation = "";
	}
}

/** validate correct attribute being used to display recent comments */
export function validateRecentComments(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	// it's possible the path ends in an abstract class, in which case we just use the reflection type rather than domain type
	const { extendedPath, id } = key;
	const hasChildComments = reflectionHints.hasCommentPaths(extendedPath.endDomainType || extendedPath.endType);

	if (!hasChildComments && (id === ATTR_RECENT_CHILD_COMMENTS)) {
		const commentsPath = extendedPath.plus("Child:DocumentsAndComments");
		const replaceKey = key.copy({
			extendedPath: commentsPath,
			id: ATTR_RECENT_COMMENTS,
		});

		if (replaceKey.reflectionAttribute) {
			return createSearchItem(attributeKey, null, null, replaceKey.typedString);
		}
	} else if (hasChildComments && (id === ATTR_RECENT_COMMENTS)) {
		const replaceKey = key.copy({
			extendedPath: extendedPath.domainPath,
			id: ATTR_RECENT_CHILD_COMMENTS,
		});

		if (replaceKey.reflectionAttribute) {
			return createSearchItem(attributeKey, null, null, replaceKey.typedString);
		}
	}
}

/** validate that the referenced image label is valid */
export function validateImageLabel(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { extendedPath, index, isImageLabel } = key;
	
	if (isImageLabel && !extendedPath.endClass.imageLabelEnum?.hasActiveValue(index.toString())) {
		if (index) {
			return createRemoveItem(attributeKey);
		} else if (!hasHeading(key, attributesStore)) {
			// some views use Heading Id="Images" Index="", so don't raise issue in that case
			const replaceKey = key.copy({
				index: null,
			});
	
			return createSearchItem(attributeKey, containerUrl, null, replaceKey.typedString);
		}
	}
}

/** validate that the key references a valid ColorMaterialSamples enum value */
export function validateColorMaterialSampleKey(containerUrl: cnl, attributeKey: C8AttributeKey, key: Key, attributesStore: CustomViewAttributesStore): CustomViewMigrationKeyValidationResult {
	const { extendedPath, id, index } = key;
	
	if ((id === "ColorMaterialSamples") && isDefined(index) && (extendedPath.endType === "ColorMaterial")) {
		const enumList = getEnumList("ColorMaterialSamples");

		if (!enumList.hasActiveValue(index.toString())) {
			return createRemoveItem(attributeKey);
		}
	}
}

validations.push(["Invalid key", validateSyntax]);
validations.push(["Path not in model", validatePathInModel]);
validations.push(["Path not typed", validatePathTyped]);
validations.push(["Path types changed", validatePathChangedType]);
validations.push(["Path omitted", validatePathNotOmitted]);
validations.push(["Invalid key in matrix", validateKeyInMatrix]);
validations.push(["Invalid comments attribute", validateRecentComments]);
validations.push(["Invalid attribute", validateAttributeExists]);
validations.push(["Path not in view", validatePathInView]); // appears after invalid attribute since it can affect the end type of the path
validations.push(["Disabled attribute", validateAttributeEnabled]);
validations.push(["Hidden attribute", validateAttributeVisible]);
validations.push(["Invalid property", validateProperty]);
validations.push(["Invalid name", validateName]);
validations.push(["Invalid image label", validateImageLabel]);
validations.push(["Invalid ColorMaterialSample key", validateColorMaterialSampleKey]);

// TODO:
//
// PreferenceView#load -
//		update typed path to latest (if not already being done)
//		matrix filter key does not start with matrixTarget type
//		group attributes are boolean, enum, ref
//		report attributes are numeric
//
// GetValidatedCustomView -
//		image label does not exist
//

